import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { FunctionalSelect, SelectItem } from './ui/Select';
import { getRoleCatalog } from '../services/rolesService';
import { getCookie, setCookie, deleteCookie } from '../services/cookieService';
import { organizationsService, SELECTED_ORGANIZATION_EVENT } from '../services/organizationsService';
import { membershipsOf, firstAllowedPath, resolveSelectedOrgId } from '../services/permissions';

/** Same cookie the backend reads (ROLE_OVERRIDE_COOKIE); JSON {"roles": [...]}. */
export const ROLE_OVERRIDE_COOKIE = 'cube_role_override';

function readOverrideRoles() {
  try {
    const raw = getCookie(ROLE_OVERRIDE_COOKIE);
    const parsed = raw ? JSON.parse(raw) : null;
    return Array.isArray(parsed?.roles) ? parsed.roles : null;
  } catch {
    return null;
  }
}

/**
 * Platform-admin test bar: switch the selected organization and "view as" a role set.
 * The roles are sent to the backend as a cookie, which drops the admin bypass for that
 * session, so pages, buttons and 403s behave exactly as they would for that role.
 */
function AdminToolbar() {
  const { user, reloadUser } = useAuth();
  const navigate = useNavigate();
  const [catalog, setCatalog] = useState(null);
  const [draft, setDraft] = useState(() => readOverrideRoles() || []);
  const [applying, setApplying] = useState(false);
  const [orgId, setOrgId] = useState(() => organizationsService.getSelectedOrganization('id'));

  const active = Array.isArray(user?.role_override);
  const memberships = membershipsOf(user);
  const orgs = memberships.map((m) => ({ id: String(m.organization_id), name: m.organization_name }));
  const selectedOrgId = resolveSelectedOrgId(user, orgId);

  useEffect(() => {
    if (!user?.can_switch_roles) return undefined;
    getRoleCatalog().then(setCatalog).catch(() => setCatalog({ roles: [] }));
    const sync = () => setOrgId(organizationsService.getSelectedOrganization('id'));
    window.addEventListener(SELECTED_ORGANIZATION_EVENT, sync);
    return () => window.removeEventListener(SELECTED_ORGANIZATION_EVENT, sync);
  }, [user?.can_switch_roles]);

  if (!user?.can_switch_roles) return null;

  const applyRoles = async (roles) => {
    setApplying(true);
    try {
      if (roles === null) deleteCookie(ROLE_OVERRIDE_COOKIE);
      else setCookie(ROLE_OVERRIDE_COOKIE, JSON.stringify({ roles }), 1);
      const next = await reloadUser();
      // Land on a page the new role can see.
      navigate(firstAllowedPath(next, resolveSelectedOrgId(next, orgId)), { replace: true });
    } finally {
      setApplying(false);
    }
  };

  const toggleRole = (key) => {
    setDraft((prev) => (prev.includes(key) ? prev.filter((r) => r !== key) : [...prev, key]));
  };

  const handleOrgChange = (value) => {
    const parsed = organizationsService.parseOrganizationOptionValue(value);
    const nextId = parsed ? parsed.id : value;
    organizationsService.setSelectedOrganization(nextId, 'id', orgs);
  };

  const dirty = JSON.stringify(draft) !== JSON.stringify(user.role_override || []);

  return (
    <div
      className={`flex flex-wrap items-center gap-x-3 gap-y-2 border-b px-4 py-2 text-xs md:px-8 ${
        active ? 'border-amber-300 bg-amber-50 text-amber-900' : 'border-hairline bg-[#F4F7FB] text-zinc-600'
      }`}
      data-testid="admin-toolbar"
    >
      <span className="inline-flex items-center gap-1 font-semibold uppercase tracking-wide">
        {active ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
        {active ? 'Viewing as role' : 'Admin test bar'}
      </span>

      <FunctionalSelect
        label="Org"
        className="w-56"
        triggerClassName="h-8 text-xs"
        value={organizationsService.getSelectedOptionValue(orgs, selectedOrgId, 'id')}
        onValueChange={handleOrgChange}
        placeholder="Select org"
        disabled={orgs.length === 0}
      >
        {orgs.map((o) => (
          <SelectItem key={o.id} value={organizationsService.orgToOptionValue(o)}>
            {o.name}
          </SelectItem>
        ))}
      </FunctionalSelect>

      <div className="flex flex-wrap items-center gap-1">
        {(catalog?.roles || []).map((role) => {
          const on = draft.includes(role.key);
          return (
            <button
              key={role.key}
              type="button"
              onClick={() => toggleRole(role.key)}
              title={role.description}
              className={`rounded-full border px-2 py-0.5 ${
                on
                  ? 'border-[#0062FB] bg-[#0062FB] text-white'
                  : 'border-hairline bg-surface text-zinc-600 hover:bg-[#E8EEF5]'
              }`}
            >
              {role.label}
            </button>
          );
        })}
      </div>

      <div className="ml-auto flex items-center gap-2">
        <button
          type="button"
          onClick={() => applyRoles(draft)}
          disabled={applying || !dirty}
          className="inline-flex items-center gap-1 rounded-md bg-[#0062FB] px-3 py-1 font-medium text-white hover:bg-[#0052E0] disabled:opacity-50"
        >
          {applying ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
          {draft.length === 0 ? 'View with no roles' : 'View as'}
        </button>
        {active ? (
          <button
            type="button"
            onClick={() => {
              setDraft([]);
              applyRoles(null);
            }}
            disabled={applying}
            className="rounded-md border border-amber-400 px-3 py-1 font-medium text-amber-900 hover:bg-amber-100 disabled:opacity-50"
          >
            Back to admin
          </button>
        ) : null}
      </div>
    </div>
  );
}

export default AdminToolbar;
